import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiAlertCircle, FiArrowRight, FiEdit3 } from 'react-icons/fi';
import { BsFileEarmarkCheck } from 'react-icons/bs';

const pluralize = (count, singular, plural) => (count === 1 ? singular : plural);

// Descreve cada categoria: como virar texto a partir da contagem, ícone
// (mesmo usado pro item equivalente no menu do professor) e destino do botão
// de resolver. Só Redações e Atividades: é o que /dashboards/cards de fato
// retorna hoje (junto com projetos, que a usuária decidiu não listar aqui).
const ITEM_DEFS = [
  {
    key: 'redacoes',
    icon: FiEdit3,
    to: '/redacao',
    boldText: count => `${count} ${pluralize(count, 'redação', 'redações')}`,
    detailText: 'para revisar',
    description: 'Já foram corrigidas pela T.I.A. Agora é sua vez.'
  },
  {
    key: 'atividades',
    icon: BsFileEarmarkCheck,
    to: '/atividade-complementar',
    boldText: count => `${count} ${pluralize(count, 'atividade', 'atividades')}`,
    detailText: 'para avaliar',
    description: 'Atividades complementares aguardando correção.'
  }
];

// Mesma fonte já usada antes em "Para corrigir" (tema default).
async function loadCounts(client) {
  const { data, error } = await client.fetch('/dashboards/cards');

  if (error) {
    toast.error('Erro ao buscar pendências');
  }

  return {
    redacoes: data?.['surveys_count'] || 0,
    atividades: data?.['activities_count'] || 0
  };
}

function AttentionItemRow({ def, count, onResolve }) {
  const Icon = def.icon;

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#dfe3e8] bg-white p-3 shadow-sm">
      <div className="flex min-w-0 items-center gap-3">
        <Icon
          className="h-5 w-5 shrink-0 text-neutral-500"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="mb-0 text-sm">
            <span
              className="font-extrabold"
              style={{ color: '#222731' }}
            >
              {def.boldText(count)}
            </span>{' '}
            <span className="font-normal text-neutral-500">{def.detailText}</span>
          </p>
          <p className="mb-0 text-xs text-neutral-500">{def.description}</p>
        </div>
      </div>

      <Link
        to={def.to}
        onClick={onResolve}
        aria-label={`Resolver: ${def.boldText(count)} ${def.detailText}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-transform duration-100 active:scale-95"
        style={{ borderColor: 'color-mix(in srgb, var(--color-mission) 48%, transparent)' }}
      >
        <FiArrowRight
          className="h-4 w-4"
          style={{ color: 'var(--color-mission-dark)' }}
        />
      </Link>
    </div>
  );
}

export function AttentionItems() {
  const client = useClient();
  const [counts, setCounts] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());

  const load = useCallback(async () => {
    const result = await loadCounts(client);
    setCounts(result);
  }, [client]);

  useEffect(() => {
    load();
  }, [load]);

  if (!counts) return null;

  const items = ITEM_DEFS.map(def => ({ def, count: counts[def.key] })).filter(item => item.count > 0 && !dismissed.has(item.def.key));

  const resolve = key => setDismissed(prev => new Set(prev).add(key));

  if (items.length === 0) {
    return (
      <div
        className="relative mb-6 flex items-center gap-3 rounded-[24px] p-4 shadow-card"
        style={{ background: 'var(--color-bg-teal-soft)' }}
        data-testid="attention-items-empty"
      >
        <span
          aria-hidden="true"
          className="text-2xl"
        >
          🎉
        </span>
        <p
          className="mb-0 text-sm font-bold"
          style={{ color: '#222731' }}
        >
          Muito bom! Sem pendências por hoje — pode focar no resto do dia.
        </p>
      </div>
    );
  }

  const totalCount = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div
      className="relative mb-6 flex flex-col gap-3 rounded-[24px] p-4 shadow-card"
      style={{ background: '#FFE9D5' }}
      data-testid="attention-items"
    >
      <div className="flex items-center gap-2">
        <FiAlertCircle
          className="h-5 w-5 shrink-0"
          style={{ color: 'var(--color-mission-dark)' }}
          aria-hidden="true"
        />
        <p
          className="mb-0 text-base font-extrabold"
          style={{ color: '#222731' }}
        >
          {totalCount} {pluralize(totalCount, 'item', 'itens')} precisam da sua atenção
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {items.map(item => (
          <AttentionItemRow
            key={item.def.key}
            def={item.def}
            count={item.count}
            onResolve={() => resolve(item.def.key)}
          />
        ))}
      </div>
    </div>
  );
}
